import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Bell,
  BellRing,
  BookOpen,
  Cake,
  CalendarDays,
  Flower2,
  Gem,
  Landmark,
  Swords,
  Users,
  type LucideIcon,
  ChevronLeft,
  ChevronRight,
  Grid3x3,
  List,
  Search,
  Sparkles,
  X,
} from 'lucide-react';
import {
  HISTORISCHE_EREIGNISSE,
  KALENDER_ART_LABELS,
  faelltAuf,
  istRunderJahrestag,
  jahrestagText,
  plusTage,
  type HistorischeKategorie,
  type KalenderArt,
  type Kalendertag,
} from '@genealogy-app/shared';
import { AppLayout } from '@/components/Layout/AppLayout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  fetchCalendarEvents,
  fetchKalenderAbo,
  saveKalenderAbo,
  sendeKalenderVorschau,
  type CalendarEvent,
  type KalenderAboEinstellungen,
} from '@/api/calendar.api';

// ─── Konstanten ──────────────────────────────────────────────────────────────

const MONATE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];
const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const ART_STIL: Record<KalenderArt, { punkt: string; chip: string; text: string; Icon: LucideIcon }> = {
  birthday: { punkt: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-800 ring-emerald-200', text: 'text-emerald-700', Icon: Cake },
  death_anniversary: { punkt: 'bg-slate-500', chip: 'bg-slate-100 text-slate-700 ring-slate-200', text: 'text-slate-600', Icon: Flower2 },
  marriage_anniversary: { punkt: 'bg-violet-500', chip: 'bg-violet-50 text-violet-800 ring-violet-200', text: 'text-violet-700', Icon: Gem },
  historical: { punkt: 'bg-amber-500', chip: 'bg-amber-50 text-amber-800 ring-amber-200', text: 'text-amber-700', Icon: Landmark },
};

const KATEGORIE_ICON: Record<HistorischeKategorie, LucideIcon> = {
  krieg: Swords,
  politik: Landmark,
  gesellschaft: Users,
  forschung: BookOpen,
};

function ArtIcon({ art, className }: { art: KalenderArt; className?: string }) {
  const Icon = ART_STIL[art].Icon;
  return <Icon className={className} aria-hidden="true" />;
}

function KategorieIcon({ kategorie, className }: { kategorie: HistorischeKategorie; className?: string }) {
  const Icon = KATEGORIE_ICON[kategorie];
  return <Icon className={className} aria-hidden="true" />;
}

type Ansicht = 'monat' | 'jahr' | 'liste';

// ─── Ein Eintrag, wie die Seite ihn zeigt ────────────────────────────────────
//
// Familie und Zeitgeschichte in einer Form – dann gibt es eine Liste, einen
// Filter und eine Darstellung statt zwei nebeneinanderherlaufender.

interface Eintrag {
  key: string;
  art: KalenderArt;
  month: number;
  day: number;
  year: number;
  titel: string;
  lebt: boolean;
  personIds: string[];
  Icon: LucideIcon;
  hinweis?: string;
}

function ausFamilie(ev: CalendarEvent): Eintrag {
  return {
    key: ev.id,
    art: ev.type,
    month: ev.month,
    day: ev.day,
    year: ev.year,
    titel: ev.personName,
    lebt: ev.lebt,
    personIds: ev.personIds,
    Icon: ART_STIL[ev.type].Icon,
  };
}

const HISTORISCH: Eintrag[] = HISTORISCHE_EREIGNISSE.map((h) => ({
  key: `h-${h.year}-${h.month}-${h.day}-${h.titel}`,
  art: 'historical',
  month: h.month,
  day: h.day,
  year: h.year,
  titel: h.titel,
  lebt: false,
  personIds: [],
  Icon: KATEGORIE_ICON[h.kategorie],
  hinweis: h.hinweis,
}));

// ─── Datumshilfen ────────────────────────────────────────────────────────────

function heuteTag(): Kalendertag {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
}

function tageImMonat(jahr: number, monat: number): number {
  return new Date(jahr, monat, 0).getDate();
}

/** Montag = 0 … Sonntag = 6 */
function ersterWochentag(jahr: number, monat: number): number {
  return (new Date(jahr, monat - 1, 1).getDay() + 6) % 7;
}

function gleicherTag(a: Kalendertag | null, b: Kalendertag): boolean {
  return !!a && a.year === b.year && a.month === b.month && a.day === b.day;
}

function tagLang(t: Kalendertag): string {
  return new Date(t.year, t.month - 1, t.day).toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function relativ(t: Kalendertag, heute: Kalendertag): string {
  const diff = Math.round(
    (new Date(t.year, t.month - 1, t.day).getTime() - new Date(heute.year, heute.month - 1, heute.day).getTime()) /
      86_400_000,
  );
  if (diff === 0) return 'Heute';
  if (diff === 1) return 'Morgen';
  if (diff > 1 && diff < 7) return `in ${diff} Tagen`;
  return new Date(t.year, t.month - 1, t.day).toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' });
}

function anlass(e: Eintrag, jahr: number): string {
  return jahrestagText(e.art, jahr - e.year, e.lebt);
}

// ─── Kleine Bausteine ────────────────────────────────────────────────────────

function RundMarke() {
  return (
    <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-px text-[10px] font-semibold text-amber-800">
      <Sparkles className="h-2.5 w-2.5" aria-hidden="true" />
      rund
    </span>
  );
}

function EintragZeile({
  e,
  jahr,
  onPerson,
  datum,
  kompakt = false,
}: {
  e: Eintrag;
  jahr: number;
  onPerson: (id: string) => void;
  datum?: ReactNode;
  kompakt?: boolean;
}) {
  const jahre = jahr - e.year;
  const rund = istRunderJahrestag(jahre);
  const stil = ART_STIL[e.art];
  const klickbar = e.personIds.length > 0;
  const Inhalt = (
    <>
      <e.Icon className={`mt-0.5 h-4 w-4 shrink-0 ${stil.text}`} aria-hidden="true" />
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-baseline gap-x-1.5">
          <span className={`font-medium text-slate-800 ${klickbar ? 'group-hover:text-blue-700 group-hover:underline' : ''}`}>
            {e.titel}
          </span>
          {rund && jahre > 0 && <RundMarke />}
        </span>
        <span className={`block text-xs ${stil.text}`}>
          {e.art === 'historical' ? `${e.year} · vor ${jahre} Jahren` : `${anlass(e, jahr)} · ${e.year}`}
        </span>
        {!kompakt && e.hinweis && <span className="mt-0.5 block text-xs text-slate-500">{e.hinweis}</span>}
      </span>
      {datum && <span className="shrink-0 text-right text-xs text-slate-400">{datum}</span>}
    </>
  );
  return klickbar ? (
    <button
      type="button"
      onClick={() => onPerson(e.personIds[0])}
      className="group flex w-full items-start gap-2.5 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-slate-50"
      title="Im Stammbaum zeigen"
    >
      {Inhalt}
    </button>
  ) : (
    <div className="flex items-start gap-2.5 px-2 py-1.5 text-sm">{Inhalt}</div>
  );
}

// ─── Monatsansicht ───────────────────────────────────────────────────────────

function MonatsRaster({
  jahr,
  monat,
  eintraegeAm,
  heute,
  gewaehlt,
  onWaehle,
}: {
  jahr: number;
  monat: number;
  eintraegeAm: (t: Kalendertag) => Eintrag[];
  heute: Kalendertag;
  gewaehlt: Kalendertag | null;
  onWaehle: (t: Kalendertag) => void;
}) {
  const leer = ersterWochentag(jahr, monat);
  const tage = tageImMonat(jahr, monat);
  const zellen: ReactNode[] = [];
  for (let i = 0; i < leer; i++) zellen.push(<div key={`l${i}`} className="bg-slate-50" />);

  for (let day = 1; day <= tage; day++) {
    const t = { year: jahr, month: monat, day };
    const liste = eintraegeAm(t);
    const istHeute = gleicherTag(heute, t);
    const istGewaehlt = gleicherTag(gewaehlt, t);
    const wochenende = (leer + day - 1) % 7 >= 5;
    const sichtbar = liste.slice(0, 3);
    zellen.push(
      <button
        key={day}
        type="button"
        onClick={() => onWaehle(t)}
        className={[
          'group flex min-h-[3.25rem] flex-col gap-0.5 bg-white p-1 text-left transition-colors sm:min-h-[6.5rem] sm:p-1.5',
          'hover:bg-blue-50/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400',
          istGewaehlt ? 'ring-2 ring-inset ring-blue-500' : '',
          wochenende ? 'sm:bg-slate-50/40' : '',
        ].join(' ')}
        aria-label={`${day}. ${MONATE[monat - 1]}: ${liste.length} Ereignisse`}
      >
        <span
          className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
            istHeute ? 'bg-blue-600 text-white' : 'text-slate-600'
          }`}
        >
          {day}
        </span>
        {/* Mobil: nur Punkte. Ab sm: Namen. */}
        <span className="flex flex-wrap gap-0.5 sm:hidden">
          {liste.slice(0, 4).map((e) => (
            <span key={e.key} className={`h-1.5 w-1.5 rounded-full ${ART_STIL[e.art].punkt}`} />
          ))}
        </span>
        <span className="hidden w-full flex-col gap-0.5 sm:flex">
          {sichtbar.map((e) => {
            const rund = istRunderJahrestag(jahr - e.year);
            return (
              <span
                key={e.key}
                className={`flex w-full items-center gap-1 truncate rounded px-1 py-px text-[11px] ring-1 ring-inset ${ART_STIL[e.art].chip} ${
                  rund ? 'font-semibold' : ''
                }`}
                title={`${e.titel} – ${anlass(e, jahr)}`}
              >
                <e.Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
                <span className="truncate">{e.titel}</span>
              </span>
            );
          })}
          {liste.length > 3 && (
            <span className="px-1 text-[10px] font-medium text-slate-400">+{liste.length - 3} weitere</span>
          )}
        </span>
      </button>,
    );
  }

  // Die letzte Woche auffüllen, sonst schaut dort das Gitter grau durch.
  const rest = (7 - ((leer + tage) % 7)) % 7;
  for (let i = 0; i < rest; i++) zellen.push(<div key={`r${i}`} className="bg-slate-50" />);

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
        {WOCHENTAGE.map((w) => (
          <div key={w} className="py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px bg-slate-200">{zellen}</div>
    </div>
  );
}

// ─── Jahresansicht ───────────────────────────────────────────────────────────

function MiniMonat({
  jahr,
  monat,
  eintraegeAm,
  heute,
  gewaehlt,
  onWaehle,
  onMonat,
}: {
  jahr: number;
  monat: number;
  eintraegeAm: (t: Kalendertag) => Eintrag[];
  heute: Kalendertag;
  gewaehlt: Kalendertag | null;
  onWaehle: (t: Kalendertag) => void;
  onMonat: () => void;
}) {
  const leer = ersterWochentag(jahr, monat);
  const tage = tageImMonat(jahr, monat);
  let summe = 0;
  const zellen: ReactNode[] = [];
  for (let i = 0; i < leer; i++) zellen.push(<div key={`l${i}`} />);
  for (let day = 1; day <= tage; day++) {
    const t = { year: jahr, month: monat, day };
    const liste = eintraegeAm(t);
    summe += liste.length;
    const arten = [...new Set(liste.map((e) => e.art))].slice(0, 3);
    const istHeute = gleicherTag(heute, t);
    const istGewaehlt = gleicherTag(gewaehlt, t);
    zellen.push(
      <button
        key={day}
        type="button"
        onClick={() => onWaehle(t)}
        className={`flex flex-col items-center rounded-md py-0.5 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
          istGewaehlt ? 'bg-blue-50 ring-2 ring-blue-500' : ''
        }`}
      >
        <span
          className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${
            istHeute ? 'bg-blue-600 font-semibold text-white' : liste.length ? 'font-semibold text-slate-800' : 'text-slate-500'
          }`}
        >
          {day}
        </span>
        <span className="flex h-1.5 gap-px">
          {arten.map((a) => (
            <span key={a} className={`h-1.5 w-1.5 rounded-full ${ART_STIL[a].punkt}`} />
          ))}
        </span>
      </button>,
    );
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
      <button
        type="button"
        onClick={onMonat}
        className="mb-2 flex w-full items-baseline justify-between rounded px-1 text-left hover:text-blue-700"
        title="Monat öffnen"
      >
        <span className="text-sm font-semibold text-slate-800">{MONATE[monat - 1]}</span>
        <span className="text-[11px] text-slate-400">{summe || ''}</span>
      </button>
      <div className="mb-1 grid grid-cols-7">
        {WOCHENTAGE.map((w) => (
          <div key={w} className="text-center text-[10px] font-medium text-slate-400">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-y-0.5">{zellen}</div>
    </div>
  );
}

// ─── Erinnerungen per E-Mail ─────────────────────────────────────────────────

function ErinnerungenDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const { data: abo, isLoading } = useQuery({ queryKey: ['calendar-abo'], queryFn: fetchKalenderAbo });
  const [form, setForm] = useState<KalenderAboEinstellungen | null>(null);

  useEffect(() => {
    if (abo && !form) {
      setForm({
        // Wer den Dialog öffnet, will in aller Regel einschalten – außer es
        // gibt noch gar keinen Mailserver.
        aktiv: abo.aktiv || abo.versandBereit,
        arten: abo.arten,
        rhythmus: abo.rhythmus,
        vorlaufTage: abo.vorlaufTage,
        nurRunde: abo.nurRunde,
        nurLebende: abo.nurLebende,
      });
    }
  }, [abo, form]);

  useEffect(() => {
    const taste = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, [onClose]);

  const speichern = useMutation({
    mutationFn: saveKalenderAbo,
    onSuccess: (neu) => {
      queryClient.setQueryData(['calendar-abo'], neu);
      toast.success(neu.aktiv ? 'Erinnerungen eingeschaltet.' : 'Erinnerungen ausgeschaltet.');
      onClose();
    },
    onError: (e: Error) => toast.error(e.message || 'Speichern fehlgeschlagen.'),
  });

  const probe = useMutation({
    mutationFn: sendeKalenderVorschau,
    onSuccess: (r) =>
      toast.success(
        r.ereignisse > 0
          ? `Probe verschickt – mit ${r.ereignisse} Ereignis${r.ereignisse === 1 ? '' : 'sen'} der nächsten 7 Tage.`
          : 'Probe verschickt. In den nächsten 7 Tagen steht nichts an.',
      ),
    onError: (e: Error) => toast.error(e.message || 'Probe fehlgeschlagen.'),
  });

  const setze = <K extends keyof KalenderAboEinstellungen>(k: K, v: KalenderAboEinstellungen[K]) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));

  const toggleArt = (a: KalenderArt) =>
    setForm((f) => {
      if (!f) return f;
      const arten = f.arten.includes(a) ? f.arten.filter((x) => x !== a) : [...f.arten, a];
      return { ...f, arten };
    });

  return (
    <div className="fixed inset-0 z-[1400] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="erinnerungen-titel"
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <h2 id="erinnerungen-titel" className="flex items-center gap-2 text-base font-semibold text-slate-900">
              <BellRing className="h-4 w-4 text-blue-600" aria-hidden="true" />
              Erinnerungen per E-Mail
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Eine Mail am Morgen, nur an Tagen, an denen etwas ansteht.
            </p>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>

        {isLoading || !form || !abo ? (
          <div className="flex h-48 items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <div className="space-y-5 px-5 py-4 text-sm">
              {!abo.versandBereit && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Es ist noch kein Mailserver eingerichtet. Deine Einstellungen werden gespeichert, Mails kommen aber
                  erst, wenn der Betreiber den Versand einrichtet.
                </div>
              )}

              <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2.5">
                <span>
                  <span className="block font-medium text-slate-800">Erinnerungen erhalten</span>
                  {abo.email && <span className="block text-xs text-slate-500">an {abo.email}</span>}
                </span>
                <input
                  type="checkbox"
                  className="h-5 w-5 accent-blue-600"
                  checked={form.aktiv}
                  onChange={(e) => setze('aktiv', e.target.checked)}
                />
              </label>

              <fieldset disabled={!form.aktiv} className="space-y-5 disabled:opacity-50">
                <div>
                  <legend className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Woran erinnern?</legend>
                  <div className="grid grid-cols-2 gap-2">
                    {(Object.keys(KALENDER_ART_LABELS) as KalenderArt[]).map((a) => (
                      <label
                        key={a}
                        className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 ${
                          form.arten.includes(a) ? 'border-blue-300 bg-blue-50' : 'border-slate-200'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="accent-blue-600"
                          checked={form.arten.includes(a)}
                          onChange={() => toggleArt(a)}
                        />
                        <ArtIcon art={a} className={`h-4 w-4 ${ART_STIL[a].text}`} />
                        <span className="text-slate-700">{KALENDER_ART_LABELS[a]}</span>
                      </label>
                    ))}
                  </div>
                  {form.arten.includes('historical') && (
                    <p className="mt-1.5 text-xs text-slate-500">Zeitgeschichte nur zu runden Jahrestagen.</p>
                  )}
                </div>

                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Wie oft?</p>
                  <div className="grid grid-cols-2 gap-2">
                    {(
                      [
                        ['taeglich', 'Am Tag selbst', 'oder ein paar Tage vorher'],
                        ['woechentlich', 'Wochenübersicht', 'jeden Montag'],
                      ] as const
                    ).map(([wert, titel, sub]) => (
                      <label
                        key={wert}
                        className={`flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 ${
                          form.rhythmus === wert ? 'border-blue-300 bg-blue-50' : 'border-slate-200'
                        }`}
                      >
                        <input
                          type="radio"
                          name="rhythmus"
                          className="mt-1 accent-blue-600"
                          checked={form.rhythmus === wert}
                          onChange={() => setze('rhythmus', wert)}
                        />
                        <span>
                          <span className="block text-slate-800">{titel}</span>
                          <span className="block text-xs text-slate-500">{sub}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                  {form.rhythmus === 'taeglich' && (
                    <label className="mt-2 flex items-center justify-between gap-3 text-slate-700">
                      Erinnern
                      <select
                        value={form.vorlaufTage}
                        onChange={(e) => setze('vorlaufTage', Number(e.target.value))}
                        className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
                      >
                        <option value={0}>am Tag selbst</option>
                        <option value={1}>einen Tag vorher</option>
                        <option value={3}>drei Tage vorher</option>
                        <option value={7}>eine Woche vorher</option>
                      </select>
                    </label>
                  )}
                </div>

                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Eingrenzen</p>
                  <label className="flex cursor-pointer items-start gap-2">
                    <input
                      type="checkbox"
                      className="mt-0.5 accent-blue-600"
                      checked={form.nurRunde}
                      onChange={(e) => setze('nurRunde', e.target.checked)}
                    />
                    <span>
                      <span className="block text-slate-800">Nur runde Jahrestage</span>
                      <span className="block text-xs text-slate-500">10., 20., 25., 50., 100. … – gut für große Bäume</span>
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-start gap-2">
                    <input
                      type="checkbox"
                      className="mt-0.5 accent-blue-600"
                      checked={form.nurLebende}
                      onChange={(e) => setze('nurLebende', e.target.checked)}
                    />
                    <span>
                      <span className="block text-slate-800">Geburtstage nur von Lebenden</span>
                      <span className="block text-xs text-slate-500">Todes- und Hochzeitstage bleiben unberührt</span>
                    </span>
                  </label>
                </div>
              </fieldset>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3">
              <button
                type="button"
                onClick={() => probe.mutate(form)}
                disabled={!abo.versandBereit || probe.isPending || form.arten.length === 0}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                title="Eine Probe mit den nächsten sieben Tagen an dich schicken"
              >
                {probe.isPending ? 'Wird verschickt…' : 'Probe senden'}
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={() => speichern.mutate(form)}
                  disabled={speichern.isPending || (form.aktiv && form.arten.length === 0)}
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {speichern.isPending ? 'Wird gespeichert…' : 'Speichern'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── CalendarPage ────────────────────────────────────────────────────────────

const ALLE_ARTEN: KalenderArt[] = ['birthday', 'death_anniversary', 'marriage_anniversary', 'historical'];

export function CalendarPage() {
  const navigate = useNavigate();
  const heute = useMemo(heuteTag, []);

  const [ansicht, setAnsicht] = useState<Ansicht>('monat');
  const [jahr, setJahr] = useState(heute.year);
  const [monat, setMonat] = useState(heute.month);
  const [gewaehlt, setGewaehlt] = useState<Kalendertag | null>(heute);
  const [arten, setArten] = useState<Set<KalenderArt>>(new Set(ALLE_ARTEN));
  const [nurRunde, setNurRunde] = useState(false);
  const [suche, setSuche] = useState('');
  const [erinnerungenOffen, setErinnerungenOffen] = useState(false);

  const { data: events = [], isLoading, isError } = useQuery({
    queryKey: ['calendar-events'],
    queryFn: fetchCalendarEvents,
  });
  const { data: abo } = useQuery({ queryKey: ['calendar-abo'], queryFn: fetchKalenderAbo, staleTime: 60_000 });

  const alle = useMemo(() => [...events.map(ausFamilie), ...HISTORISCH], [events]);

  const zaehler = useMemo(() => {
    const z: Record<KalenderArt, number> = { birthday: 0, death_anniversary: 0, marriage_anniversary: 0, historical: 0 };
    for (const e of alle) z[e.art] += 1;
    return z;
  }, [alle]);

  // Was die Filter durchlassen – unabhängig vom Tag.
  const gefiltert = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return alle.filter((e) => {
      if (!arten.has(e.art)) return false;
      if (q && !e.titel.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [alle, arten, suche]);

  // Index nach (Monat, Tag) – die Raster fragen 365-mal.
  const nachTag = useMemo(() => {
    const m = new Map<string, Eintrag[]>();
    for (const e of gefiltert) {
      const k = `${e.month}-${e.day}`;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(e);
    }
    return m;
  }, [gefiltert]);

  const eintraegeAm = useCallback(
    (t: Kalendertag): Eintrag[] => {
      const liste = [...(nachTag.get(`${t.month}-${t.day}`) ?? [])];
      // 29. Februar in Nicht-Schaltjahren am 28. zeigen.
      if (t.month === 2 && t.day === 28) {
        for (const e of nachTag.get('2-29') ?? []) if (faelltAuf(e.month, e.day, t)) liste.push(e);
      }
      return liste
        .filter((e) => t.year - e.year >= 0 && (!nurRunde || istRunderJahrestag(t.year - e.year)))
        .sort((a, b) => ALLE_ARTEN.indexOf(a.art) - ALLE_ARTEN.indexOf(b.art) || a.year - b.year);
    },
    [nachTag, nurRunde],
  );

  // Demnächst: die nächsten 30 Tage ab heute.
  const demnaechst = useMemo(() => {
    const out: { tag: Kalendertag; e: Eintrag }[] = [];
    for (let i = 0; i < 30 && out.length < 40; i++) {
      const t = plusTage(heute, i);
      for (const e of eintraegeAm(t)) {
        // Zeitgeschichte nur rund – sonst überdeckt sie die Familie.
        if (e.art === 'historical' && !istRunderJahrestag(t.year - e.year)) continue;
        if (t.year - e.year <= 0) continue;
        out.push({ tag: t, e });
      }
    }
    return out;
  }, [eintraegeAm, heute]);

  const zeigePerson = useCallback((id: string) => navigate(`/?person=${encodeURIComponent(id)}`), [navigate]);

  const blaettern = (richtung: -1 | 1) => {
    if (ansicht === 'monat') {
      let m = monat + richtung;
      let j = jahr;
      if (m < 1) { m = 12; j -= 1; }
      if (m > 12) { m = 1; j += 1; }
      setMonat(m);
      setJahr(j);
    } else {
      setJahr((j) => j + richtung);
    }
  };

  const zuHeute = () => {
    setJahr(heute.year);
    setMonat(heute.month);
    setGewaehlt(heute);
  };

  const waehle = (t: Kalendertag) => {
    setGewaehlt((alt) => (gleicherTag(alt, t) ? null : t));
  };

  const toggleArt = (a: KalenderArt) =>
    setArten((alt) => {
      const neu = new Set(alt);
      if (neu.has(a)) neu.delete(a);
      else neu.add(a);
      return neu;
    });

  const tagesListe = gewaehlt ? eintraegeAm(gewaehlt) : [];

  // Zeitgeschehen im Jahr der Familienereignisse des gewählten Tages.
  const zeitgeschehen = useMemo(() => {
    const jahre = new Set(tagesListe.filter((e) => e.art !== 'historical').map((e) => e.year));
    return HISTORISCHE_EREIGNISSE.filter((h) => jahre.has(h.year));
  }, [tagesListe]);

  const titel = ansicht === 'monat' ? `${MONATE[monat - 1]} ${jahr}` : String(jahr);

  return (
    <AppLayout>
      <div className="h-full overflow-auto bg-slate-50">
        {/* ── Kopf ── */}
        <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
            <h1 className="mr-2 flex items-center gap-2 text-base font-semibold text-slate-800">
              <CalendarDays className="h-5 w-5 text-slate-500" aria-hidden="true" />
              Familienkalender
            </h1>

            {/* Blättern */}
            <div className="flex items-center gap-1">
              <button onClick={() => blaettern(-1)} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100" aria-label="Zurück">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="min-w-[8.5rem] text-center text-sm font-semibold tabular-nums text-slate-800">{titel}</span>
              <button onClick={() => blaettern(1)} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100" aria-label="Weiter">
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                onClick={zuHeute}
                className="ml-1 rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
              >
                Heute
              </button>
            </div>

            {/* Ansicht */}
            <div className="flex rounded-lg bg-slate-100 p-0.5" role="tablist" aria-label="Ansicht">
              {(
                [
                  ['monat', 'Monat', CalendarDays],
                  ['jahr', 'Jahr', Grid3x3],
                  ['liste', 'Liste', List],
                ] as const
              ).map(([wert, label, Icon]) => (
                <button
                  key={wert}
                  role="tab"
                  aria-selected={ansicht === wert}
                  onClick={() => setAnsicht(wert)}
                  className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium ${
                    ansicht === wert ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                  {label}
                </button>
              ))}
            </div>

            <div className="ml-auto flex items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={suche}
                  onChange={(e) => setSuche(e.target.value)}
                  placeholder="Name suchen…"
                  className="w-36 rounded-md border border-slate-200 py-1 pl-7 pr-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 sm:w-44"
                />
              </div>
              <button
                onClick={() => setErinnerungenOffen(true)}
                className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium ${
                  abo?.aktiv
                    ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                    : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
                title="Erinnerungen per E-Mail einstellen"
              >
                {abo?.aktiv ? <BellRing className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
                <span className="hidden sm:inline">Erinnerungen</span>
              </button>
            </div>
          </div>

          {/* Filter */}
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-1.5 px-4 pb-2.5">
            {ALLE_ARTEN.map((a) => {
              const an = arten.has(a);
              return (
                <button
                  key={a}
                  onClick={() => toggleArt(a)}
                  aria-pressed={an}
                  className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset transition-colors ${
                    an ? ART_STIL[a].chip : 'bg-white text-slate-400 ring-slate-200 hover:text-slate-600'
                  }`}
                >
                  <span className={`h-2 w-2 rounded-full ${an ? ART_STIL[a].punkt : 'bg-slate-300'}`} />
                  {KALENDER_ART_LABELS[a]}
                  <span className="tabular-nums opacity-60">{zaehler[a]}</span>
                </button>
              );
            })}
            <span className="mx-1 h-4 w-px bg-slate-200" />
            <button
              onClick={() => setNurRunde((v) => !v)}
              aria-pressed={nurRunde}
              className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
                nurRunde ? 'bg-amber-100 text-amber-800 ring-amber-200' : 'bg-white text-slate-500 ring-slate-200 hover:text-slate-700'
              }`}
            >
              <Sparkles className="h-3 w-3" />
              Nur runde
            </button>
          </div>
        </div>

        {/* ── Inhalt ── */}
        <div className="mx-auto max-w-7xl px-4 py-5">
          {isLoading && (
            <div className="flex h-64 items-center justify-center">
              <LoadingSpinner />
            </div>
          )}
          {isError && <p className="py-16 text-center text-sm text-red-500">Fehler beim Laden der Ereignisse.</p>}

          {!isLoading && !isError && (
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
              <div className="min-w-0">
                {ansicht === 'monat' && (
                  <MonatsRaster
                    jahr={jahr}
                    monat={monat}
                    eintraegeAm={eintraegeAm}
                    heute={heute}
                    gewaehlt={gewaehlt}
                    onWaehle={waehle}
                  />
                )}

                {ansicht === 'jahr' && (
                  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <MiniMonat
                        key={m}
                        jahr={jahr}
                        monat={m}
                        eintraegeAm={eintraegeAm}
                        heute={heute}
                        gewaehlt={gewaehlt}
                        onWaehle={waehle}
                        onMonat={() => {
                          setMonat(m);
                          setAnsicht('monat');
                        }}
                      />
                    ))}
                  </div>
                )}

                {ansicht === 'liste' && (
                  <JahresListe jahr={jahr} eintraegeAm={eintraegeAm} onPerson={zeigePerson} heute={heute} />
                )}

                {events.length === 0 && (
                  <p className="mt-4 rounded-lg border border-dashed border-slate-300 bg-white px-4 py-3 text-sm text-slate-500">
                    Noch keine Familienereignisse: Im Kalender erscheinen Geburts-, Todes- und Hochzeitstage, die auf den
                    Tag genau bekannt sind. Angaben wie „1850" oder „ca. März 1923" bleiben bewusst draußen.
                  </p>
                )}
              </div>

              {/* ── Seitenleiste ── */}
              <aside className="space-y-4">
                {gewaehlt && (
                  <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
                    <header className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
                      <h2 className="text-sm font-semibold text-slate-800">{tagLang(gewaehlt)}</h2>
                      <button
                        onClick={() => setGewaehlt(null)}
                        className="rounded p-0.5 text-slate-400 hover:bg-slate-100"
                        aria-label="Tag schließen"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </header>
                    <div className="max-h-[28rem] overflow-y-auto p-2">
                      {tagesListe.length === 0 ? (
                        <p className="px-2 py-3 text-center text-sm text-slate-400">Keine Ereignisse an diesem Tag.</p>
                      ) : (
                        tagesListe.map((e) => (
                          <EintragZeile key={e.key} e={e} jahr={gewaehlt.year} onPerson={zeigePerson} />
                        ))
                      )}
                      {zeitgeschehen.length > 0 && (
                        <div className="mt-2 border-t border-slate-100 px-2 pt-2">
                          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                            Zeitgeschehen in diesen Jahren
                          </p>
                          <ul className="space-y-1 text-xs text-slate-600">
                            {zeitgeschehen.map((h) => (
                              <li key={`${h.year}-${h.titel}`} className="flex gap-1.5">
                                <KategorieIcon kategorie={h.kategorie} className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                                <span>
                                  <span className="font-medium tabular-nums">{h.day}.{h.month}.{h.year}</span> {h.titel}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </section>
                )}

                <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
                  <header className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
                    <h2 className="text-sm font-semibold text-slate-800">Demnächst</h2>
                    <span className="text-xs text-slate-400">30 Tage</span>
                  </header>
                  <div className="max-h-[32rem] overflow-y-auto p-2">
                    {demnaechst.length === 0 ? (
                      <p className="px-2 py-3 text-center text-sm text-slate-400">In den nächsten 30 Tagen steht nichts an.</p>
                    ) : (
                      demnaechst.map(({ tag, e }) => (
                        <EintragZeile
                          key={`${e.key}-${tag.year}`}
                          e={e}
                          jahr={tag.year}
                          onPerson={zeigePerson}
                          datum={relativ(tag, heute)}
                          kompakt
                        />
                      ))
                    )}
                  </div>
                  {!abo?.aktiv && (
                    <button
                      onClick={() => setErinnerungenOffen(true)}
                      className="flex w-full items-center justify-center gap-1.5 border-t border-slate-100 px-4 py-2.5 text-xs font-medium text-blue-700 hover:bg-blue-50"
                    >
                      <Bell className="h-3.5 w-3.5" />
                      Per E-Mail daran erinnern lassen
                    </button>
                  )}
                </section>
              </aside>
            </div>
          )}
        </div>
      </div>

      {erinnerungenOffen && <ErinnerungenDialog onClose={() => setErinnerungenOffen(false)} />}
    </AppLayout>
  );
}

// ─── Listenansicht ───────────────────────────────────────────────────────────

function JahresListe({
  jahr,
  eintraegeAm,
  onPerson,
  heute,
}: {
  jahr: number;
  eintraegeAm: (t: Kalendertag) => Eintrag[];
  onPerson: (id: string) => void;
  heute: Kalendertag;
}) {
  const monate = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const m = i + 1;
      const tage: { tag: Kalendertag; liste: Eintrag[] }[] = [];
      for (let d = 1; d <= tageImMonat(jahr, m); d++) {
        const t = { year: jahr, month: m, day: d };
        const liste = eintraegeAm(t);
        if (liste.length) tage.push({ tag: t, liste });
      }
      return { m, tage };
    }).filter((x) => x.tage.length > 0);
  }, [jahr, eintraegeAm]);

  if (monate.length === 0) {
    return <p className="rounded-xl border border-slate-200 bg-white py-12 text-center text-sm text-slate-400">Nichts gefunden.</p>;
  }

  return (
    <div className="space-y-4">
      {monate.map(({ m, tage }) => (
        <section key={m} className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <h3 className="border-b border-slate-100 px-4 py-2 text-sm font-semibold text-slate-800">
            {MONATE[m - 1]} {jahr}
          </h3>
          <ul className="divide-y divide-slate-100">
            {tage.map(({ tag, liste }) => (
              <li key={tag.day} className="flex gap-3 px-2 py-1.5">
                <span
                  className={`mt-1.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums ${
                    gleicherTag(heute, tag) ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {tag.day}
                </span>
                <div className="min-w-0 flex-1">
                  {liste.map((e) => (
                    <EintragZeile key={e.key} e={e} jahr={jahr} onPerson={onPerson} kompakt />
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
